import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, PartyPopper } from 'lucide-react';
import { PageLayout } from '@/components/ui/page-layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ListSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { useNotificationStore } from '../store/notificationStore';
import { useServerNow } from '../hooks/useServerNow';
import { NotificationRow } from '../components/NotificationRow';
import type { NotificationTab } from '../types/notification.types';

/**
 * N7 (86d3w2kf4) IN-4 — the full `/notifications` surface.
 *
 * The popover is the primary surface and deliberately does not page; this is where the whole inbox
 * lives, with the four server-counted tabs and the house "Show more" pager.
 *
 * ⚠️ REACT COMPILER (86d3d1z40). `hasMore`, `isLoading`, `isLoadingMore` and all four counts are read
 * as PRIMITIVE fields through direct selectors. `disabled={!hasMore()}` against a store getter
 * memoizes on the stable function reference and leaves Show more permanently disabled while the data
 * underneath it moves — the exact bug class this feature has already been bitten by twice.
 */
export function NotificationsPage() {
  const [searchParams] = useSearchParams();

  const items = useNotificationStore((s) => s.items);
  const tab = useNotificationStore((s) => s.tab);
  const isLoading = useNotificationStore((s) => s.isLoading);
  const isLoadingMore = useNotificationStore((s) => s.isLoadingMore);
  const hasMore = useNotificationStore((s) => s.hasMore);
  const error = useNotificationStore((s) => s.error);

  const needsCount = useNotificationStore((s) => s.needsCount);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const activeCount = useNotificationStore((s) => s.activeCount);
  const doneCount = useNotificationStore((s) => s.doneCount);

  const setTab = useNotificationStore((s) => s.setTab);
  const fetchMore = useNotificationStore((s) => s.fetchMore);

  const nowMs = useServerNow();
  const didInit = useRef(false);

  /**
   * ⛔ THE MOUNT FETCH IS CONDITIONAL, AND THAT IS THE WHOLE POINT.
   *
   * The store is app-lifetime and mounted once in `AppLayout`, with documented reset semantics: a tab
   * change resets; navigating away from a notifications surface and back does NOT. An unconditional
   * `useEffect(() => fetch(), [])` here silently defeats that — someone who pressed Show more three
   * times, opened a record, and hit Back would be dumped at the top of a 50-row list every time.
   *
   * So: an explicit `?tab=` is a navigation intent and outranks preservation; otherwise a non-empty
   * window is rendered as-is and fires ZERO requests; and only a genuinely empty window fetches,
   * landing on Needs action when there is anything to act on.
   */
  useEffect(() => {
    const store = useNotificationStore.getState();
    const requested = parseTab(searchParams.get('tab'));

    // An explicit ?tab= is a navigation INTENT and applies on EVERY change, not just the first —
    // the popover's "+N more — view all" link targets /notifications?tab=needs, and when the user
    // is already on this page that is a same-route navigation with no remount. A mount-only guard
    // silently ignored it: URL said tab=needs, page stayed on All (2026-08-25 review).
    if (requested && requested !== store.tab) {
      // `setTab` resets the window, bumps `reqSeq` and refetches — the documented reset path.
      store.setTab(requested);
      didInit.current = true;
      return;
    }

    // Everything below is genuinely mount-once: window preservation is the contract, so a Back
    // onto a loaded window must fire zero requests.
    if (didInit.current) return;
    didInit.current = true;

    if (store.items.length > 0) return;

    const preferred = requested ?? (store.needsCount > 0 ? 'needs' : 'all');
    if (preferred !== store.tab) store.setTab(preferred);
    else void store.fetch({ includePinned: true });
  }, [searchParams]);

  const counts: Record<NotificationTab, number> = {
    all: activeCount,
    needs: needsCount,
    unread: unreadCount,
    done: doneCount,
  };

  // `N remaining` is a SERVER total minus a loaded window — which is exactly why the counts ride the
  // page envelope. Clamped at zero: a mutation can retire rows between two fetches, and "-3 remaining"
  // would be a worse answer than none.
  const remaining = Math.max(counts[tab] - items.length, 0);

  return (
    <PageLayout
      title="Notifications"
      description="Everything the system has told you, and anything still waiting on a decision."
    >
      <Tabs value={tab} onValueChange={(next) => setTab(next as NotificationTab)}>
        {/* ⛔ FOUR TABS, each labelled from a SERVER count. `matchesTab()` in the store mirrors the
            repository's `ApplyTab` switch exactly; if they ever drift, a row shows up on one surface
            and not the other and nothing fails loudly. All four share the identical sort key
            (CreatedAt DESC, Id DESC) — a mixed direction makes the keyset row-value comparison return
            the WRONG row rather than an error. */}
        <TabsList>
          <TabsTrigger value="needs">
            Needs action{needsCount > 0 && ` (${needsCount})`}
          </TabsTrigger>
          <TabsTrigger value="unread">Unread{unreadCount > 0 && ` (${unreadCount})`}</TabsTrigger>
          <TabsTrigger value="all">All{activeCount > 0 && ` (${activeCount})`}</TabsTrigger>
          <TabsTrigger value="done">Done{doneCount > 0 && ` (${doneCount})`}</TabsTrigger>
        </TabsList>

        {/* ⚠️ A READING MEASURE, and LEFT-ALIGNED — no `mx-auto`. Full-bleed rows on a 1300px
            column put the title at one edge and the timestamp at the other, so scanning a list
            means sweeping the whole screen per row. 768px (`max-w-3xl`, the house measure used
            by ProfilePage and the announcement dialogs) holds a title, its body and the entity
            chip in one glance. It caps the WHOLE tab body, not just the list, so the error
            callout, the skeleton, the empty state and the load-more footer keep the same edge. */}
        <TabsContent value={tab} className="mt-4 max-w-3xl space-y-3">
          {error && <ErrorCallout message={error} />}

          {isLoading && items.length === 0 ? (
            <ListSkeleton rows={6} />
          ) : items.length === 0 ? (
            !error && <TabEmptyState tab={tab} />
          ) : (
            <>
              <ul className="space-y-2">
                {items.map((n) => (
                  // ⛔ Keyed by `id`, never `jobId` — `jobId` is nullable by schema.
                  <li key={n.id}>
                    <NotificationRow notification={n} nowMs={nowMs} variant="full" />
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-xs text-muted-foreground">
                  Showing {items.length} of {counts[tab]}
                </p>

                {/* ⛔ A BUTTON, NOT INFINITE SCROLL. `IntersectionObserver` appears zero times in this
                    codebase; the house load-more precedent is `ActivityList.tsx`. And there is no
                    Previous button because keyset paging is forward-only — the design comp's
                    Previous/Next pair is a hardcoded-disabled stub, not a specification. */}
                {hasMore && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isLoadingMore}
                    onClick={() => void fetchMore()}
                  >
                    {isLoadingMore ? 'Loading…' : `Show more (${remaining} remaining)`}
                  </Button>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
}

/**
 * ⚠️ Inline and destructive-toned, never a toast.
 *
 * A list that failed to load has to say so where the list would be, with the retry beside it. Note
 * what does NOT reach here: a stale pagination cursor. The store recognises that as
 * `StaleCursorError` and resets to page 1 in silence, because a cursor-version bump on deploy day is
 * not something to show every scrolled user a red banner about.
 */
function ErrorCallout({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/8 p-3">
      <p className="text-xs text-destructive">{message}</p>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1.5 h-7 px-2 text-xs"
        onClick={() => void useNotificationStore.getState().fetch({ includePinned: true })}
      >
        Retry
      </Button>
    </div>
  );
}

/** Per-tab empty copy — "nothing here" means something different on each of the four. */
function TabEmptyState({ tab }: { tab: NotificationTab }) {
  switch (tab) {
    case 'needs':
      return (
        <EmptyState
          icon={PartyPopper}
          title="Nothing needs your attention"
          description="Postings that fail or need reconciling will appear here."
          className="py-12"
        />
      );
    case 'unread':
      return (
        <EmptyState
          icon={PartyPopper}
          title="All caught up"
          description="You have read everything in your inbox."
          className="py-12"
        />
      );
    case 'done':
      return (
        <EmptyState
          icon={Inbox}
          title="Nothing archived yet"
          description="Notifications you mark as done are filed here."
          className="py-12"
        />
      );
    default:
      return (
        <EmptyState
          icon={PartyPopper}
          title="All caught up"
          description="Postings you submit will show up here."
          className="py-12"
        />
      );
  }
}

/**
 * Reads a `?tab=` value, rejecting anything that is not one of the four.
 *
 * ⛔ Never cast the raw string. It reaches `setTab` and from there the server's `ApplyTab`, and an
 * unrecognised value would fall through the switch to the `all` branch — a link that silently shows
 * the wrong slice is worse than one that ignores its parameter.
 */
function parseTab(raw: string | null): NotificationTab | null {
  return raw === 'all' || raw === 'needs' || raw === 'unread' || raw === 'done' ? raw : null;
}
